import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Modal,
  Table,
  Tag,
  Input,
  Space,
  Tooltip,
  message,
} from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileProtectOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DateCustom from "../../../../hook/DateCustom";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import {
  enrichCase,
  exportCasesExcel,
  getRemainDebt,
  getDebt,
  loadLoanByContno,
  STATUS_GROUPS,
} from "../lawyerCaseUtils";

const tagConfig = {
  all: {
    color: "#3357FF",
    textColor: "#fff",
    icon: <InboxOutlined />,
    label: "ทั้งหมด",
  },
  waitLawsuit: {
    color: "#ff4d4f",
    textColor: "#fff",
    icon: <ClockCircleOutlined />,
    label: "สร้างฟ้อง",
  },
  withBackCase: {
    color: "#fa8c16",
    textColor: "#fff",
    icon: <CalendarOutlined />,
    label: "มีวันนัดศาล",
  },
  withRedCase: {
    color: "#1677ff",
    textColor: "#fff",
    icon: <CheckCircleOutlined />,
    label: "ศาลตัดสิน/พิพากษา",
  },
  ageeement: {
    color: "#13c2c2",
    textColor: "#fff",
    icon: <FileProtectOutlined />,
    label: "ยอมความ",
  },
  finish: {
    color: "#52c41a",
    textColor: "#fff",
    icon: <CheckOutlined />,
    label: "จบคดี",
  },
  withdrawCase: {
    color: "#8c8c8c",
    textColor: "#fff",
    icon: <CloseCircleOutlined />,
    label: "ถอนฟ้อง",
  },
};

const DataCheck = ({ open, close, data, status, lawyerName, subtitle }) => {
  const [, convertDateThaiShort] = DateCustom();
  const [, currencyFormatComma] = CurrencyFormat();
  const [loanByContno, setLoanByContno] = useState({});
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [exporting, setExporting] = useState(false);

  // รายการคดีของสถานะที่เลือก
  const dataArr = useMemo(
    () => (data && status ? data[status] || [] : []),
    [data, status],
  );

  useEffect(() => {
    if (open) {
      setKeyword("");
      loadLoanData();
    }
  }, [open, dataArr]);

  // โหลดข้อมูลสัญญาจาก POST_LOAN_DB2 ตามเลขสัญญา
  const loadLoanData = async () => {
    setLoading(true);
    try {
      setLoanByContno(await loadLoanByContno(dataArr));
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response ? error.response.data : error.message,
      );
      setLoanByContno({});
      message.error("ไม่พบข้อมูลสัญญา");
    } finally {
      setLoading(false);
    }
  };

  const tableData = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    return dataArr
      .map((item) => enrichCase(item, loanByContno))
      .filter((item) => {
        if (!search) return true;
        const name = `${item.CUSTOMER_FNAME || ""} ${item.CUSTOMER_LNAME || ""}`;
        return (
          item.CONTNO?.toLowerCase().includes(search) ||
          name.toLowerCase().includes(search) ||
          item.black_case_number?.toLowerCase().includes(search) ||
          item.red_case_number?.toLowerCase().includes(search)
        );
      });
  }, [dataArr, loanByContno, keyword]);

  const money = (value) =>
    value !== null && value !== undefined && value !== ""
      ? currencyFormatComma(value)
      : "-";

  // ค่าขาดประโยชน์อยู่ที่ระดับจำเลย (work_judge_defendants)
  // จำเลยคนเดียวแสดงค่าเลย ถ้าหลายคนแสดงแยกบรรทัด จ.1, จ.2, ...
  // ทำยอม -> แสดง "-"
  const renderDefendants = (record, getValue) => {
    const defendants = record.defendants || [];
    if (record.settlement || defendants.length === 0) return "-";
    if (defendants.length === 1) return getValue(defendants[0]);
    return (
      <div style={{ lineHeight: 1.6 }}>
        {defendants.map((d) => (
          <Tooltip
            key={d.id}
            title={`${d.SNAM || ""}${d.NAME1 || ""} ${d.NAME2 || ""}`.trim()}
          >
            <div style={{ whiteSpace: "nowrap" }}>
              <span style={{ color: "#8c8c8c" }}>
                จ.{d.defendant_number ?? "-"}{" "}
              </span>
              {getValue(d)}
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      align: "center",
      fixed: "left",
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      width: 120,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>{text || "-"}</span>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "customer",
      width: 200,
      render: (_, record) =>
        `${record.CUSTOMER_TNAME || ""}${record.CUSTOMER_FNAME || ""} ${
          record.CUSTOMER_LNAME || ""
        }`.trim() || "-",
    },
    {
      title: "สาขา",
      key: "LOCAT",
      width: 80,
      align: "center",
      render: (_, record) => record.db2?.LOAN?.LOCAT || "-",
    },
    {
      title: "สถานะล่าสุด",
      dataIndex: "main_status_description",
      key: "main_status_description",
      width: 150,
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "ฟ้อง",
      key: "lawsuit",
      children: [
        {
          title: "ศาล",
          dataIndex: "provincial_court",
          key: "provincial_court",
          width: 140,
          render: (text) => text || "-",
        },
        {
          title: "คดีดำ",
          dataIndex: "black_case_number",
          key: "black_case_number",
          width: 130,
          render: (text) => text || "-",
        },
        {
          title: "วันฟ้อง",
          dataIndex: "date_of_plaint",
          key: "date_of_plaint",
          width: 110,
          align: "center",
          render: (text) => (text ? convertDateThaiShort(text) : "-"),
        },
        {
          title: "ทุนทรัพย์ฟ้อง",
          dataIndex: "litigation_funds",
          key: "litigation_funds",
          width: 120,
          align: "right",
          render: (value) => money(value),
        },
        {
          title: "ค่าใช้จ่าย (เบิก / จ่ายจริง)",
          key: "expense",
          width: 160,
          align: "right",
          render: (_, record) => {
            const expenses = record.work_expense_logs || [];
            if (expenses.length === 0) return "-";
            return (
              <Tooltip
                title={
                  <div>
                    {expenses.map((e) => (
                      <div key={e.id}>
                        {e.expense_description || e.expense_name}: เบิก{" "}
                        {money(e.withdraw)} / จ่าย {money(e.pay)}
                      </div>
                    ))}
                  </div>
                }
              >
                <div style={{ cursor: "help", lineHeight: 1.6 }}>
                  <div>{money(record.expenseWithdraw)}</div>
                  <div style={{ color: "#389e0d" }}>
                    {money(record.expensePay)}
                  </div>
                </div>
              </Tooltip>
            );
          },
        },
      ],
    },
    {
      title: "ชั้นศาล",
      key: "court",
      children: [
        {
          title: "คดีแดง",
          key: "red_case_number",
          width: 130,
          render: (_, record) => (
            <span style={{ color: "#cf1322" }}>
              {record.latestJudge?.red_case_number || "-"}
            </span>
          ),
        },
        {
          title: "วันพิพากษา",
          key: "judge_date",
          width: 110,
          align: "center",
          render: (_, record) =>
            record.latestJudge?.judge_date
              ? convertDateThaiShort(record.latestJudge.judge_date)
              : "-",
        },
        {
          title: status === "ageeement" ? "ยอดทำยอม" : "ยอดพิพากษา",
          key: "judgement",
          width: 120,
          align: "right",
          render: (_, record) => money(record.latestJudge?.judgement),
        },
        {
          title: "ค่าขาดประโยชน์",
          key: "cost_of_uselessness",
          width: 120,
          align: "right",
          render: (_, record) =>
            renderDefendants(record, (d) => money(d.cost_of_uselessness)),
        },
        {
          title: "ค่าขาดประโยชน์รายเดือน",
          key: "cost_of_useleseness_per_month",
          width: 140,
          align: "right",
          render: (_, record) =>
            renderDefendants(record, (d) =>
              money(d.cost_of_useleseness_per_month),
            ),
        },
        {
          title: "จำนวนเดือน",
          key: "cost_of_useleseness_month",
          width: 100,
          align: "center",
          render: (_, record) =>
            renderDefendants(record, (d) => d.cost_of_useleseness_month ?? "-"),
        },
      ],
    },
    {
      title: "ข้อมูลสัญญา",
      key: "loan",
      children: [
        {
          title: "ยอดหนี้",
          key: "NCSHPRC",
          width: 110,
          align: "right",
          render: (_, record) =>
            record.db2
              ? record.db2.LOAN?.DATA_TYPE === "lsfhp-h-ksm" ||
                record.db2.LOAN?.DATA_TYPE === "lsfhp-ksm" ||
                record.db2.LOAN?.DATA_TYPE === "rpsl" ||
                record.db2.LOAN?.DATA_TYPE === "rpsl-h"
                ? currencyFormatComma(record.db2.LOAN?.NCSHPRC)
                : currencyFormatComma(record.db2.LOAN?.TOTPRC)
              : "-",
        },
        {
          title: "จ่ายมาทั้งหมด",
          key: "SMPAY",
          width: 110,
          align: "right",
          render: (_, record) =>
            record.db2 ? currencyFormatComma(record.db2.LOAN?.SMPAY) : "-",
        },
        {
          title: "ยอดหนี้คงเหลือ",
          key: "remainDebt",
          width: 120,
          align: "right",
          render: (_, record) => {
            const remain = getRemainDebt(record.db2);
            if (remain === null) return "-";
            return (
              <span
                style={{
                  fontWeight: 600,
                  color: remain > 0 ? "#cf1322" : "#389e0d",
                }}
              >
                {remain < 0 ? "-" : ""}
                {currencyFormatComma(Math.abs(remain))}
              </span>
            );
          },
        },
        {
          title: "จ่ายล่าสุด",
          key: "LPAYD",
          width: 110,
          align: "center",
          render: (_, record) =>
            record.db2?.LOAN?.LPAYD
              ? convertDateThaiShort(record.db2.LOAN.LPAYD)
              : "-",
        },
      ],
    },
    {
      title: "หมายเหตุ",
      key: "memo",
      width: 220,
      render: (_, record) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.5,
          }}
        >
          {record.latestMemo || "-"}
        </div>
      ),
    },
  ];

  const config = tagConfig[status] || tagConfig.all;

  // ยอดรวมท้ายตาราง (ตามข้อมูลที่แสดงอยู่)
  const sumBy = (fn) =>
    Math.round(
      tableData.reduce((sum, r) => {
        const n = Number(fn(r));
        return sum + (isNaN(n) ? 0 : n);
      }, 0) * 100,
    ) / 100;
  const sumDefendants = (field) =>
    sumBy((r) =>
      r.settlement
        ? 0
        : (r.defendants || []).reduce((s, d) => s + Number(d[field] || 0), 0),
    );

  const totals = {
    litigation_funds: currencyFormatComma(sumBy((r) => r.litigation_funds || 0)),
    expense: (
      <div style={{ lineHeight: 1.6 }}>
        <div>{currencyFormatComma(sumBy((r) => r.expenseWithdraw))}</div>
        <div style={{ color: "#389e0d" }}>
          {currencyFormatComma(sumBy((r) => r.expensePay))}
        </div>
      </div>
    ),
    judgement: currencyFormatComma(sumBy((r) => r.latestJudge?.judgement || 0)),
    cost_of_uselessness: currencyFormatComma(
      sumDefendants("cost_of_uselessness"),
    ),
    cost_of_useleseness_per_month: currencyFormatComma(
      sumDefendants("cost_of_useleseness_per_month"),
    ),
    NCSHPRC: currencyFormatComma(sumBy((r) => getDebt(r.db2) || 0)),
    SMPAY: currencyFormatComma(sumBy((r) => r.db2?.LOAN?.SMPAY || 0)),
    remainDebt: currencyFormatComma(sumBy((r) => getRemainDebt(r.db2) || 0)),
  };

  const leafColumns = columns.flatMap((col) => col.children || [col]);

  const renderSummary = () =>
    tableData.length > 0 && (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: "#fff7e6", fontWeight: 700 }}>
          {leafColumns.map((col, index) =>
            index === 0 ? (
              <Table.Summary.Cell key="label" index={0} colSpan={3}>
                รวม {tableData.length} สัญญา
              </Table.Summary.Cell>
            ) : index < 3 ? null : (
              <Table.Summary.Cell
                key={col.key}
                index={index}
                align={col.align === "right" ? "right" : undefined}
              >
                {totals[col.key] ?? ""}
              </Table.Summary.Cell>
            ),
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );

  // export ตามตาราง (ข้อมูลที่กรองค้นหาอยู่)
  const onExport = async () => {
    setExporting(true);
    try {
      const group = STATUS_GROUPS.find((g) => g.key === status);
      const name = group?.label || config.label;
      await exportCasesExcel({
        sheets: [{ name, color: group?.color || "FF3357FF", rows: tableData }],
        subtitle,
        formatDate: convertDateThaiShort,
        fileName: `รายงานคดี_${lawyerName || ""}_${name}_${dayjs().format(
          "YYYYMMDD_HHmm",
        )}.xlsx`.replace(/[\\/]/g, "-"),
      });
    } catch (error) {
      console.error(error);
      message.error("Export Excel ไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MailOutlined style={{ color: "#1890ff" }} />
          รายละเอียดข้อมูล
        </div>
      }
      open={open}
      width={1200}
      onCancel={() => close(false)}
      destroyOnClose
      footer={[
        <Button key="close" danger onClick={() => close(false)}>
          ปิด
        </Button>,
      ]}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Tag
          color={config.color}
          style={{
            fontSize: 16,
            padding: "8px 14px",
            borderRadius: 10,
            fontWeight: "bold",
            color: config.textColor,
          }}
        >
          {config.icon} {config.label}: {dataArr.length} สัญญา
        </Tag>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหาเลขสัญญา / ชื่อ / เลขคดี"
            style={{ width: 260 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined spin={loading} />}
            disabled={loading}
            onClick={loadLoanData}
          >
            รีโหลด
          </Button>
          <Button
            type="primary"
            style={{ background: "#217346" }}
            icon={<FileExcelOutlined />}
            loading={exporting}
            disabled={loading || tableData.length === 0}
            onClick={onExport}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      <Table
        size="small"
        bordered
        rowKey={(record) => record.id ?? record.CONTNO}
        loading={loading}
        columns={columns}
        dataSource={tableData}
        scroll={{ x: 2820 }}
        pagination={false}
        summary={renderSummary}
        locale={{
          emptyText: (
            <div style={{ padding: 40, color: "#bfbfbf" }}>
              <InboxOutlined style={{ fontSize: 48 }} />
              <div style={{ marginTop: 8 }}>ไม่มีข้อมูล</div>
            </div>
          ),
        }}
      />

      <div style={{ marginTop: 12, color: "#cf1322", fontSize: 13 }}>
        * หมายเหตุ: จ.1 คือ จำเลยที่ 1, จ.2 คือ จำเลยที่ 2 (ตามลำดับจำเลย)
      </div>
    </Modal>
  );
};

export default DataCheck;
